import React, { useCallback } from 'react';
import { PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';

const SIBLING_RANGE = 2;

interface PaginationControlsProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

const makePageLink = (pageNum: number, currentPage: number, onPageChange: PaginationControlsProps['onPageChange']) => (
  <PaginationLink
    key={pageNum}
    href="#"
    isActive={currentPage === pageNum}
    onClick={(e) => {
      e.preventDefault();
      onPageChange(pageNum);
    }}>
    {pageNum}
  </PaginationLink>
);

export const PaginationControls = ({ page, pageCount, onPageChange }: PaginationControlsProps) => {
  const navButtonBaseClasses = 'border-[#173737] bg-[#F5FFFF] text-[#135959] hover:opacity-80';

  const renderPaginationItems = useCallback(() => {
    const items: React.ReactNode[] = [];
    const startWindow = Math.max(1, page - SIBLING_RANGE);
    const endWindow = Math.min(pageCount, page + SIBLING_RANGE);
    const showAll = pageCount <= 2 * SIBLING_RANGE + 3;

    if (showAll) {
      for (let i = 1; i <= pageCount; i++) {
        items.push(makePageLink(i, page, onPageChange));
      }
      return items;
    }

    items.push(makePageLink(1, page, onPageChange));

    if (startWindow > 2) {
      items.push(<PaginationEllipsis key="ellipsis-start" />);
    }

    for (let i = startWindow; i <= endWindow; i++) {
      if (i === 1 || i === pageCount) continue;
      items.push(makePageLink(i, page, onPageChange));
    }

    if (endWindow < pageCount - 1) {
      items.push(<PaginationEllipsis key="ellipsis-end" />);
    }

    if (pageCount > 1) {
      items.push(makePageLink(pageCount, page, onPageChange));
    }

    return items;
  }, [page, pageCount, onPageChange]);

  if (pageCount <= 1) {
    return null;
  }

  return (
    <section className="mt-sh-30">
      <div className="flex w-full items-center justify-between">
        <PaginationPrevious
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (page > 1) {
              onPageChange(page - 1);
            }
          }}
          className={`${navButtonBaseClasses} ${
            page === 1
              ? 'pointer-events-none border-[#717182] bg-[#E9EAEB] text-people-gray-500 [&_svg]:text-[#A6A6A6]'
              : ''
          }`}
        />

        <div className="flex items-center gap-1">{renderPaginationItems()}</div>

        <PaginationNext
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (page < pageCount) {
              onPageChange(page + 1);
            }
          }}
          className={`${navButtonBaseClasses} ${
            page === pageCount
              ? 'pointer-events-none border-[#717182] bg-[#E9EAEB] text-people-gray-500 [&_svg]:text-[#A6A6A6]'
              : ''
          }`}
        />
      </div>
    </section>
  );
};
